import { ArrowRight, BarChart3, ClipboardCheck, Clock, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useReports, type ReportSummary } from "@/hooks/useReports";

/** From mockups/03-report-catalog.html. Cards grouped by category. */

const ICONS: Record<string, LucideIcon> = {
  "quality-audits": ClipboardCheck,
};

function ReportCard({ report }: { report: ReportSummary }) {
  const Icon = ICONS[report.key] ?? BarChart3;

  const body = (
    <>
      <div className="flex items-start gap-4">
        <div className="gradient-band flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-slate-800">{report.name}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">{report.description}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        {report.isReady ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            Refreshed daily
          </span>
        ) : (
          <span className="pill bg-slate-100 text-slate-500">Coming soon</span>
        )}
        {report.isReady && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-rose">
            Open <ArrowRight className="h-4 w-4" />
          </span>
        )}
      </div>
    </>
  );

  const shell = "rounded-xl border border-slate-200 bg-white p-5 transition-shadow";

  // A card for a report that is not ready is deliberately NOT a link. A dead link that
  // looks live is how people learn to distrust a dashboard.
  return report.isReady ? (
    <Link to={`/reports/${report.key}`} className={`${shell} block hover:shadow-md`}>
      {body}
    </Link>
  ) : (
    <div className={`${shell} opacity-70`}>{body}</div>
  );
}

export function ReportCatalog() {
  const { user } = useAuth();
  const { reports, loading } = useReports();

  const categories = [...new Set(reports.map((r) => r.category))];

  return (
    <div className="px-4 py-6 lg:px-8 lg:py-8">
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          Welcome back, <span className="font-semibold text-slate-700">{user?.displayName}</span>.
        </p>
      </div>

      {loading && <p className="text-sm text-slate-400">Loading…</p>}

      {!loading && reports.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">No reports yet</h3>
          {/* Honest about the actual state: the reports are still being built. Saying
              "contact your administrator" here would send people chasing a grant that
              would not help them. */}
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            Your account is set up and signed in. The reports are still being built — Quality
            Audits is next.
          </p>
        </div>
      )}

      {categories.map((category) => (
        <section key={category} className="mb-8">
          <p className="eyebrow mb-3">{category}</p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {reports
              .filter((r) => r.category === category)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((r) => (
                <ReportCard key={r.key} report={r} />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}
